import { useEffect } from "react";
import { Link } from "react-router-dom";
import { X, ExternalLink, BookOpen, ArrowRight, ShieldCheck, Zap } from "lucide-react";

// Educational quick-reference for NFPA 70 Article 760 + NYC electrical code amendments.
// All summaries authored by APM — no copyrighted text reproduced. Linked out to
// NFPA's official free public access for the full code text.

const NFPA70_REFERENCES = [
  {
    section: "Article 760 (NEC) — Fire Alarm Systems",
    paragraphs: [
      "Article 760 is the section of NFPA 70 that governs the installation of wiring and equipment for fire alarm systems. It is the primary code reference for fire alarm circuit wiring on every NICET and S-98 exam.",
      "Article 760 separates fire alarm circuits into two power-limited categories — PLFA (Power-Limited Fire Alarm) and NPLFA (Non-Power-Limited Fire Alarm) — based on whether the source is energy-limited per the listed device specs.",
    ],
  },
  {
    section: "Cable Type Markings (760.179 / 760.176)",
    paragraphs: [
      "PLFA cables are marked FPL (general use), FPLR (riser-rated, vertical building penetration), or FPLP (plenum-rated, environmental air-handling spaces).",
      "Cable hierarchy: FPLP can substitute for FPLR or FPL; FPLR can substitute for FPL; FPL cannot substitute upward. This is on virtually every fire-alarm wiring exam.",
    ],
  },
  {
    section: "Separation from Power Conductors (760.136)",
    paragraphs: [
      "Power-limited fire alarm cables must be installed so they are physically separated from power, lighting, Class 1, and medium/high-voltage conductors — typically by maintaining a 2-inch minimum separation or by installing the FPL conductors in a separate raceway, junction box, or cable tray.",
      "Exception: if the power conductors are separated from the FPL by a fixed insulating barrier, or if the FPL is enclosed in a metallic raceway, separation rules are relaxed.",
    ],
  },
  {
    section: "Mechanical Protection & Support (760.24, 760.130)",
    paragraphs: [
      "FPL cables must be installed in a neat and workmanlike manner, supported by listed cable ties / clamps / hangers at intervals that prevent damage.",
      "Where exposed to physical damage (e.g., the lower 7 ft of a wall), additional mechanical protection (raceway, conduit, or armored cable) is required.",
    ],
  },
  {
    section: "Grounding & Bonding (760.32, 250)",
    paragraphs: [
      "All metallic raceways, enclosures, and shields used for fire alarm circuits must be effectively grounded per NFPA 70 Article 250.",
      "Ground-fault detection is required at the panel — a single ground fault must annunciate as a trouble condition (also enforced by NFPA 72 §10.17).",
    ],
  },
];

const NYC_AMENDMENTS = [
  {
    section: "Red Cable Jacket Requirement (NYC Electrical Code Amendment to 760)",
    paragraphs: [
      "New York City requires that all fire alarm cable jackets be RED in color so any electrician on the job can immediately identify life-safety circuits — this is a NYC-specific amendment beyond standard NFPA 70.",
      "Field tip: if you see a non-red jacket on a fire-alarm circuit in NYC, document it as a violation on your acceptance test.",
    ],
  },
  {
    section: "Metal Raceway Mandate (NYC)",
    paragraphs: [
      "NYC amendments require fire alarm circuits to be installed in metallic raceway (EMT, IMC, RMC, or rigid steel) in many occupancies — this is stricter than standard NFPA 70 which permits FPL cable in some open-cable applications.",
      "The metal raceway provides both mechanical protection and an additional EMI shield for the data-carrying SLC and NAC circuits.",
    ],
  },
  {
    section: "Surge Protection (NYC SPD requirements)",
    paragraphs: [
      "NYC requires Surge Protective Devices (SPD) at the FACU service entrance and on all off-premises communication circuits, exceeding the baseline NFPA 70 requirements.",
      "SPDs protect the panel from utility-line transients, lightning-induced surges, and equipment switching events.",
    ],
  },
  {
    section: "Service & Disconnect Requirements",
    paragraphs: [
      "FACU primary power must come from a dedicated branch circuit, breaker locked or labeled in the ON position, marked 'FIRE ALARM CIRCUIT — DO NOT DISCONNECT.'",
      "NYC enforces this with its own labeling and lock-out provisions — verify with your local FDNY inspector during acceptance testing.",
    ],
  },
  {
    section: "Battery & Standby Power",
    paragraphs: [
      "Standby power (sealed lead-acid battery) must be sized for 24 hr supervisory + 5 min alarm at full alarm load (15 min for voice EVACS systems per NFPA 72 §24).",
      "NYC adds the requirement that battery rooms or enclosures meet additional ventilation standards for any installation > 24V / multiple cabinets.",
    ],
  },
];

export default function Nfpa70Modal({ open, onClose }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4 sm:p-8"
      style={{ background: "rgba(9,9,11,0.7)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="nfpa70-modal-overlay"
    >
      <div
        className="bg-white border-4 border-red-600 max-w-3xl w-full max-h-[88vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="nfpa70-modal"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 sm:px-8 pt-6 pb-4 border-b-2 border-zinc-100 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <BookOpen className="w-4 h-4 text-red-600" />
              <div className="overline"><span className="red-bar" />NFPA 70 Quick Reference</div>
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tighter">
              NYC Electrical Code · Article 760
            </h2>
            <p className="text-sm text-zinc-600 mt-1.5 leading-relaxed">
              Educational summary of the NFPA 70 sections and NYC amendments most-tested on the FDNY S-98. Authored by APM. Not a substitute for the code book.
            </p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-red-600 flex-shrink-0" data-testid="nfpa70-modal-close" aria-label="Close">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto px-6 sm:px-8 py-6 flex-1" data-testid="nfpa70-modal-content">
          {/* NFPA 70 base */}
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-red-600" />
            <h3 className="font-heading text-lg font-bold tracking-tight">NFPA 70 — Article 760 essentials</h3>
          </div>
          <div className="space-y-4 mb-7">
            {NFPA70_REFERENCES.map((ref) => (
              <div key={ref.section} className="border-l-2 border-red-200 pl-4">
                <div className="font-mono text-[11px] uppercase tracking-widest text-red-600 font-bold mb-1.5">{ref.section}</div>
                {ref.paragraphs.map((p, i) => (
                  <p key={`${ref.section}-p${i}`} className="text-sm text-zinc-700 leading-relaxed mb-2 last:mb-0">{p}</p>
                ))}
              </div>
            ))}
          </div>

          {/* NYC amendments */}
          <div className="flex items-center gap-2 mb-3 pt-3 border-t-2 border-zinc-100">
            <ShieldCheck className="w-4 h-4 text-red-600" />
            <h3 className="font-heading text-lg font-bold tracking-tight">NYC Electrical Code — amendments beyond NFPA 70</h3>
          </div>
          <div className="space-y-4 mb-2">
            {NYC_AMENDMENTS.map((ref) => (
              <div key={ref.section} className="border-l-2 border-red-200 pl-4">
                <div className="font-mono text-[11px] uppercase tracking-widest text-red-600 font-bold mb-1.5">{ref.section}</div>
                {ref.paragraphs.map((p, i) => (
                  <p key={`${ref.section}-p${i}`} className="text-sm text-zinc-700 leading-relaxed mb-2 last:mb-0">{p}</p>
                ))}
              </div>
            ))}
          </div>

          {/* Copyright disclaimer */}
          <div className="mt-6 p-4 bg-zinc-50 border border-zinc-200 text-xs text-zinc-600 leading-relaxed" data-testid="nfpa70-disclaimer">
            <strong className="text-zinc-900">Copyright notice:</strong> NFPA 70 (National Electrical Code) is © National Fire Protection Association. The full code text is not reproduced here. The summaries above are APM's plain-language explanations for educational purposes. For binding code language, consult the official NFPA 70 publication or the NYC Department of Buildings electrical code page.
          </div>
        </div>

        {/* Footer actions */}
        <div className="px-6 sm:px-8 py-4 border-t-2 border-zinc-100 bg-zinc-50/50 flex flex-col sm:flex-row gap-2 flex-shrink-0">
          <a
            href="https://www.nfpa.org/codes-and-standards/nfpa-70-standard-development/70"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary text-sm justify-center flex-1"
            data-testid="nfpa70-open-official"
          >
            <ExternalLink className="w-4 h-4" /> Open the official NFPA 70 page
          </a>
          <a
            href="https://www.nyc.gov/site/buildings/codes/electrical-code.page"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline text-sm justify-center"
            data-testid="nfpa70-open-nyc"
          >
            <ExternalLink className="w-4 h-4" /> NYC Electrical Code
          </a>
          <Link
            to="/lesson/m6-l2"
            onClick={onClose}
            className="btn-outline text-sm justify-center"
            data-testid="nfpa70-open-lesson"
          >
            Module 6 · L2 lesson <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
